import { DateTime } from 'luxon';
import { Bdm1Outcome, NachversandKind, RetrievalMode } from '@prisma/client';
import { ZONE } from '../enum/ZONE';

/**
 * The BDM timeline.
 *
 * Every offset below is a fixed number of days from Cohort.bdmStartDate (BDM day 0 =
 * participant-info day) and is therefore identical for the whole cohort — nothing is
 * derived from the moment a single participant's outcome was detected.
 *
 * This is the single source for backend and frontend — reminder mails, reminder SMS and
 * the waiting screens — so the offsets and the "the deadline day still counts" rule
 * exist exactly once.
 *
 * BDM1_DEADLINE_DAY_OFFSET carries three roles at once: the number rendered in the
 * invitation and reminder texts, the end of the BDM1 survey window (end of day 13), and
 * the day the BDM exclusion becomes active (see BdmExclusionJobService).
 */

// BDM1 survey ladder. The cron schedule itself (how often the job runs) lives in
// cron-job.schedules.ts.
export const BDM1_INVITATION_DAY_OFFSET = 0;
export const BDM1_REMINDER_1_DAY_OFFSET = 2;
export const BDM1_REMINDER_2_DAY_OFFSET = 4;
export const BDM1_REMINDER_3_DAY_OFFSET = 6;
export const BDM1_REMINDER_4_DAY_OFFSET = 12;

/**
 * Survey window in days from the invitation (day 0) to the deadline. Shown in the texts:
 * invitation = this total, reminders = remaining days (constant − dayOffset). Changing it
 * updates emails + reminder SMS automatically (SMS via the Twilio {{1}} variable); a new SID
 * is only needed for wording changes.
 *
 * Unlike every other constant here this is a day COUNT, not a day index — the window
 * covers days 0-13, so do not pass it to bdmDeadline() (that would yield end of day 14).
 */
export const BDM1_DEADLINE_DAY_OFFSET = 14;

/** The parts that hand out a sample; PART2 does not, so it is no key here. */
export type SampleKind = Extract<NachversandKind, 'BDM1' | 'BDM2'>;

export const SAMPLE_KINDS: readonly SampleKind[] = [
  NachversandKind.BDM1,
  NachversandKind.BDM2,
];

// 1g sample after BDM1: reminders, then the two delivery deadlines.
export const BDM1_SAMPLE_REMINDER_1_DAY_OFFSET = 15;
export const BDM1_SAMPLE_REMINDER_2_DAY_OFFSET = 22;

/** Day offset for booking the video call that delivers the BDM1 1g sample by mail. */
export const BDM1_SAMPLE_BOOKING_DEADLINE_DAY_OFFSET = 23;

/** Day offset for picking up the BDM1 1g sample in the shop. */
export const BDM1_SAMPLE_PICKUP_DEADLINE_DAY_OFFSET = 27;

// BDM2 survey ladder; BDM2_SURVEY_DAY_OFFSET is also when BDM2 becomes
// visible to the participant.
export const BDM2_SURVEY_DAY_OFFSET = 28;
export const BDM2_REMINDER_1_DAY_OFFSET = 30;
export const BDM2_REMINDER_2_DAY_OFFSET = 32;
export const BDM2_REMINDER_3_DAY_OFFSET = 34;
export const BDM2_REMINDER_4_DAY_OFFSET = 40;

/**
 * End of the BDM2 survey window: the survey opens on day 28 and has the same 14-day
 * window as BDM1, so the deadline is the end of day 41 and the BDM exclusion for a
 * missed BDM2 becomes active on day 42 (see BdmExclusionJobService). Like
 * BDM1_DEADLINE_DAY_OFFSET this is a day count, not a day index — pass (offset - 1)
 * to bdmDeadline().
 */
export const BDM2_DEADLINE_DAY_OFFSET =
  BDM2_SURVEY_DAY_OFFSET + BDM1_DEADLINE_DAY_OFFSET;

// 4g sample after BDM2: reminders, then the two delivery deadlines. The same two delivery
// deadlines are also the pickup/booking deadline shown in the BDM2_OUTCOME mail itself.
export const BDM2_SAMPLE_REMINDER_1_DAY_OFFSET = 50;
export const BDM2_SAMPLE_REMINDER_2_DAY_OFFSET = 57;

/** Day offset for booking the video call that delivers the BDM2 4g sample by mail. */
export const BDM2_SAMPLE_BOOKING_DEADLINE_DAY_OFFSET = 58;

/** Day offset for picking up the BDM2 4g sample in the shop. */
export const BDM2_SAMPLE_PICKUP_DEADLINE_DAY_OFFSET = 62;

/** The day the long-term study draws its groups, per the study's deadline plan. */
export const PART2_RANDOMIZATION_DAY_OFFSET = 48;

/** Needed for the mail and also passed into a Twilio content variable. */
export const SAMPLE_LOCATION_NAME = 'Swiss Cannabis Center Wil SG';

/**
 * End of the deadline day (23:59:59.999 Europe/Zurich); the deadline day itself still
 * counts. Computed in Europe/Zurich so the result does not depend on the server
 * timezone.
 */
/** How a BDM date is written in every participant-facing text. */
export const BDM_DATE_FORMAT = 'dd.MM.yyyy';

export function bdmDeadline(bdmStartDate: Date, dayOffset: number): DateTime {
  return DateTime.fromJSDate(bdmStartDate, { zone: ZONE })
    .plus({ days: dayOffset })
    .endOf('day');
}

/** Whether `at` lies after the deadline instant. */
export function bdmDeadlinePassed(
  deadline: DateTime,
  at: DateTime = DateTime.now(),
): boolean {
  return at.toMillis() > deadline.toMillis();
}

/**
 * Whether day `dayOffset` of the cohort's BDM timeline has begun (00:00 Europe/Zurich).
 * The counterpart to bdmDeadlinePassed: that one closes a window, this one opens it.
 * A cohort without an anchor has no timeline, so nothing has begun.
 */
export function bdmDayStarted(
  bdmStartDate: Date | null,
  dayOffset: number,
  at: DateTime = DateTime.now(),
): boolean {
  if (bdmStartDate === null) {
    return false;
  }
  return at >= bdmDeadline(bdmStartDate, dayOffset).startOf('day');
}

// The only two offsets that are looked up per part rather than read by name.
const SAMPLE_DEADLINE_OFFSETS: Record<
  SampleKind,
  { booking: number; pickup: number }
> = {
  [NachversandKind.BDM1]: {
    booking: BDM1_SAMPLE_BOOKING_DEADLINE_DAY_OFFSET,
    pickup: BDM1_SAMPLE_PICKUP_DEADLINE_DAY_OFFSET,
  },
  [NachversandKind.BDM2]: {
    booking: BDM2_SAMPLE_BOOKING_DEADLINE_DAY_OFFSET,
    pickup: BDM2_SAMPLE_PICKUP_DEADLINE_DAY_OFFSET,
  },
};

/**
 * The sample deadline of a single participant for one part: mail -> booking deadline,
 * pickup -> pickup deadline.
 *
 * The retrieval mode decides, not the outcome: a revoked Nachversand eligibility flips a
 * MAIL retrieval to PICKUP while bdm1Outcome keeps the SAMPLE_MAIL the participant won.
 * The mode is passed in rather than looked up: mail versus pickup hangs on
 * nachversandEligible, which is why the import freezes it into the retrieval row. A caller
 * holding a BDM2 outcome can read it back off the variant (RETRIEVAL_MODE_BY_BDM2_VARIANT)
 * instead of the row.
 *
 * Without a retrieval row (the outcome cron has not run yet) the outcome is the only source
 * left — that fallback is BDM1-only, BDM2 callers pass null.
 *
 * null covers two different situations: no deadline applies (VOUCHER or no outcome yet)
 * and an unconfigured cohort anchor. Callers that need to tell them apart get
 * bdm1Outcome in the same API response — a sample outcome with a null deadline means
 * the cohort anchor is missing.
 */
export function sampleDeadline(
  kind: SampleKind,
  bdmStartDate: Date | null,
  bdm1Outcome: Bdm1Outcome | null,
  retrievalMode: RetrievalMode | null,
): DateTime | null {
  if (bdmStartDate === null) {
    return null;
  }

  const part = SAMPLE_DEADLINE_OFFSETS[kind];

  if (retrievalMode !== null) {
    switch (retrievalMode) {
      case RetrievalMode.MAIL:
        return bdmDeadline(bdmStartDate, part.booking);
      case RetrievalMode.PICKUP:
        return bdmDeadline(bdmStartDate, part.pickup);
      case RetrievalMode.NONE:
        return null;
    }
  }

  if (bdm1Outcome === null) {
    return null;
  }

  switch (bdm1Outcome) {
    case Bdm1Outcome.SAMPLE_MAIL:
      return bdmDeadline(bdmStartDate, part.booking);
    case Bdm1Outcome.SAMPLE_LOCAL:
      return bdmDeadline(bdmStartDate, part.pickup);
    case Bdm1Outcome.VOUCHER:
      return null;
  }
}

/**
 * The shop pickup deadline of a sample, or null for a kind that hands out none. The
 * shipment texts name it after a parcel came back.
 */
export function samplePickupDeadline(
  kind: NachversandKind,
  bdmStartDate: Date,
): DateTime | null {
  if (kind === NachversandKind.PART2) {
    return null;
  }

  return bdmDeadline(bdmStartDate, SAMPLE_DEADLINE_OFFSETS[kind].pickup);
}
